import React, { useState } from 'react';
import { LeftOutlined, RightOutlined, CalendarOutlined, MedicineBoxOutlined, FileTextOutlined, UserOutlined, ClockCircleOutlined, SafetyOutlined } from '@ant-design/icons';
import { useLanguage } from '../../context/LanguageContext';

export default function OurFeaturesSection() {
  const { t, isRTL } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);

  const features = [
    {
      id: 1,
      icon: <CalendarOutlined className="text-4xl sm:text-5xl" />,
      title: t('comingSoon.features.items.appointment.title') || 'Easy Appointment Booking',
      description: t('comingSoon.features.items.appointment.description') || 'Schedule your appointments with just a few clicks. Choose your preferred time and doctor instantly.',
      color: 'bg-primary'
    },
    {
      id: 2,
      icon: <MedicineBoxOutlined className="text-4xl sm:text-5xl" />,
      title: t('comingSoon.features.items.consultation.title') || 'Online Consultations',
      description: t('comingSoon.features.items.consultation.description') || 'Connect with healthcare professionals from the comfort of your home through video or chat.',
      color: 'bg-secondary'
    },
    {
      id: 3,
      icon: <FileTextOutlined className="text-4xl sm:text-5xl" />,
      title: t('comingSoon.features.items.records.title') || 'Digital Medical Records',
      description: t('comingSoon.features.items.records.description') || 'Access your complete medical history, prescriptions, and reports anytime, anywhere.',
      color: 'bg-primary'
    },
    {
      id: 4,
      icon: <UserOutlined className="text-4xl sm:text-5xl" />,
      title: t('comingSoon.features.items.doctors.title') || 'Multiple Doctors',
      description: t('comingSoon.features.items.doctors.description') || 'Browse through our network of qualified healthcare professionals and specialists.',
      color: 'bg-yellow-400'
    },
    {
      id: 5,
      icon: <ClockCircleOutlined className="text-4xl sm:text-5xl" />,
      title: t('comingSoon.features.items.support.title') || '24/7 Support',
      description: t('comingSoon.features.items.support.description') || 'Get assistance whenever you need it with our round-the-clock customer support team.',
      color: 'bg-primary'
    },
    {
      id: 6,
      icon: <SafetyOutlined className="text-4xl sm:text-5xl" />,
      title: t('comingSoon.features.items.security.title') || 'Secure & Private',
      description: t('comingSoon.features.items.security.description') || 'Your health data is encrypted and protected with industry-leading security standards.',
      color: 'bg-yellow-400'
    }
  ];

  const getVisibleFeatures = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      const index = (currentIndex + i) % features.length;
      visible.push({ ...features[index], position: i });
    }
    return visible;
  };

  const handlePrev = () => setCurrentIndex((prev) => (prev - 1 + features.length) % features.length);
  const handleNext = () => setCurrentIndex((prev) => (prev + 1) % features.length);

  const visibleFeatures = getVisibleFeatures();

  // In RTL the visual "prev" arrow points right, "next" points left — swap icons
  const NextIcon   = isRTL ? <LeftOutlined  className="text-lg lg:text-xl" /> : <RightOutlined className="text-lg lg:text-xl" />;
  const PrevIcon = isRTL ? <RightOutlined className="text-lg lg:text-xl" /> : <LeftOutlined  className="text-lg lg:text-xl" />;

  return (
    <section
      dir={isRTL ? 'rtl' : 'ltr'}
      className="py-12 sm:py-16 bg-cardColor min-h-screen flex flex-col items-center justify-center"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Title */}
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-center text-primary mb-8 sm:mb-12">
          {t('comingSoon.features.title') || 'Our Features'}
        </h2>

        {/* Carousel */}
        <div className="relative flex flex-col md:flex-row items-center justify-center gap-4 md:gap-6">

          {/* Prev Arrow — desktop */}
          <button
            onClick={handlePrev}
            className="hidden md:flex flex-shrink-0 w-10 h-10 lg:w-12 lg:h-12 rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all items-center justify-center shadow-md hover:shadow-lg"
            aria-label={t('comingSoon.features.prevLabel') || 'Previous feature'}
          >
            {PrevIcon}
          </button>

          {/* Cards */}
          <div className="w-full max-w-6xl">

            {/* Desktop — 3 cards */}
            <div className="hidden md:grid md:grid-cols-3 gap-4 lg:gap-10">
              {visibleFeatures.map((feature) => (
                <div
                  key={feature.id}
                  className={`${feature.color} rounded-2xl p-6 lg:p-8 text-white shadow-lg transition-all duration-300 ${
                    feature.position === 1 ? 'transform scale-105 lg:scale-110 shadow-2xl' : ''
                  } flex flex-col items-center text-center min-h-[280px] lg:min-h-[320px]`}
                >
                  <div className="mb-3 lg:mb-4">{feature.icon}</div>
                  <h3 className="text-xl lg:text-2xl font-bold mb-2 lg:mb-3">{feature.title}</h3>
                  <p className="text-white/90 text-sm lg:text-base leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>

            {/* Mobile — 1 card */}
            <div className="md:hidden">
              <div className={`${visibleFeatures[1].color} rounded-2xl p-6 text-white shadow-lg flex flex-col items-center text-center min-h-[280px]`}>
                <div className="mb-4">{visibleFeatures[1].icon}</div>
                <h3 className="text-xl font-bold mb-3">{visibleFeatures[1].title}</h3>
                <p className="text-white/90 text-sm leading-relaxed">{visibleFeatures[1].description}</p>
              </div>

              {/* Mobile arrows */}
              <div className="flex items-center justify-center gap-4 mt-6">
                <button
                  onClick={handlePrev}
                  className="w-10 h-10 rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all flex items-center justify-center shadow-md"
                  aria-label={t('comingSoon.features.prevLabel') || 'Previous feature'}
                >
                  {!isRTL ? <LeftOutlined className="text-lg" /> : <RightOutlined className="text-lg" />}
                </button>
                <button
                  onClick={handleNext}
                  className="w-10 h-10 rounded-full border-2 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-white transition-all flex items-center justify-center shadow-md"
                  aria-label={t('comingSoon.features.nextLabel') || 'Next feature'}
                >
                  {!isRTL ? <RightOutlined className="text-lg" /> : <LeftOutlined className="text-lg" />}
                </button>
              </div>
            </div>
          </div>

          {/* Next Arrow — desktop */}
          <button
            onClick={handleNext}
            className="hidden md:flex flex-shrink-0 w-10 h-10 lg:w-12 lg:h-12 rounded-full border-2 border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-white transition-all items-center justify-center shadow-md hover:shadow-lg"
            aria-label={t('comingSoon.features.nextLabel') || 'Next feature'}
          >
            {NextIcon}
          </button>
        </div>

        {/* Dots */}
        <div className="flex justify-center gap-2 mt-6 sm:mt-8">
          {features.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2 sm:h-3 rounded-full transition-all ${
                index === currentIndex ? 'bg-primary w-6 sm:w-8' : 'w-2 sm:w-3 bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`${t('comingSoon.features.goToLabel') || 'Go to feature'} ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}